import {useSortBy, useTable,} from "react-table"
import React, {useCallback, useEffect, useMemo, useRef, useState} from "react";
import "./DataTable.css"
import {useLocation, useNavigate} from "react-router-dom";
import moment from "moment";
import {
    applyDefaultsToObject,
    bindDependents,
    getDataTablePropsDefault,
    getFinalizedColumns,
    getHeaderByName,
    getUnformattedPhoneNumber,
    getValueOrDefault,
    objectToQueryParams,
    queryParamsToObject,
    shouldHideFromUser
} from "./DataTableUtils";
import SkeletonLoader from "../SkeletonLoader";
import Pagination from "../pagination/Pagination";
import {useFindUserSettingsByGroupNameQuery} from "../userSetting/userSettingsApi";
import {computePageControl,} from "../userSetting/UserSettingUtils";
import {
    DataItem,
    DataPage,
    DataTableActions,
    DataTableColumn,
    DataTableProps,
    FilterOptions,
    getDefaultColumnVisibility
} from "./DataTableTypes";
import Button from "../form/Button";
import IconButton from "../form/IconButton";
import {FaMagnifyingGlass} from "react-icons/fa6";
import {hasPermission, jwtDecode} from "../../../helpers/utils/AccessControlUtils";
import {skipToken} from "@reduxjs/toolkit/query";
import {quickFilterParams} from "../../../helpers/constants";
import _ from "lodash";
import Preloader from "../Preloader";
import ConfirmationModal from "../../layout/modal/ConfirmationModal";
import TailSpinLoader from "../TailSpinLoader";

const updateFilterQuery = (filterKey: string, value: any, filterQuery: any, setFilterQuery: Function) => {
    const isCheckbox = value?.target?.type == 'checkbox';
    const isDate = value?.target?.type == undefined && (filterKey === 'createdDateTo' || filterKey === 'createdDateFrom' ||
        filterKey === 'paidDateFrom' || filterKey === 'paidDateTo' ||
        filterKey === 'eventDateFrom' || filterKey === 'eventDateTo' ||
        filterKey === 'deliveredDateFrom' || filterKey === 'deliveredDateTo' ||
        filterKey === 'creationDateFrom' || filterKey === 'creationDateTo' ||
        filterKey === 'lastCheckedDateFrom' || filterKey === 'lastCheckedDateTo' ||
        filterKey === 'productDeliveredDateFrom' || filterKey === 'productDeliveredDateTo' ||
        filterKey === 'transmitDateFrom' || filterKey === 'transmitDateTo' ||
        filterKey === 'reportedDateFrom' || filterKey === 'reportedDateTo' ||
        filterKey === 'resolutionDateFrom' || filterKey === 'resolutionDateTo');

    const isPhoneNo = value?.target?.placeholder === 'Enter Phone...';
    const date = moment(value).format("yyyy-MM-DD HH:mm:ss").toUpperCase()

    value =
        // 33
        (isCheckbox ? value !== undefined && value?.target?.checked : null) ??
        (isDate ? date : null) ??
        (isPhoneNo ? getUnformattedPhoneNumber(value?.target?.value) : null) ??
        value?.target?.value ??
        value;

    let tempQuery: any = {...filterQuery};
    if (value === undefined || value === '' || (Array.isArray(value) && !value.length)) {
        delete tempQuery[filterKey];
    } else {
        if (value == "INVALID DATE") {
            delete tempQuery[filterKey];
        } else {
            tempQuery[filterKey] = value;
        }
    }

    setFilterQuery(tempQuery);
};

export const DataTable: React.FC<DataTableProps> = (props: DataTableProps) => {

    const defaultPageSize = useMemo(() => 15, []);

    const location: any = useLocation();
    const navigate: any = useNavigate();
    const isExternal = location.pathname.includes('external');

    props = applyDefaultsToObject(props, getDataTablePropsDefault());

    // Only use queryParams for setting our filter if we are appending them to the URL
    // Helpful for non-primary tables on a page, e.g. search modals
    let appliedFilter = props.appendFilterToUrl ?
        queryParamsToObject(location.search) :
        props.defaultFilter || {};

    const [deleteItemList, setDeleteItemList] = useState<any>([]);

    const defaultSort = {
        isSorted: !!props.defaultSortOptions,
        sortColumn: props.defaultSortOptions?.accessor,
        sortDescending: props.defaultSortOptions?.direction === 'desc'
    };

    const searchParams = new URLSearchParams(location.search);
    const pageNum: number = parseInt(getValueOrDefault(searchParams.get('pageNumber'), '0'));

    type FilterQuery = Record<string, any>;

    let query = {};

    const [defaultFilter, setDefaultFilter] = useState(() => {
        return Object.keys(appliedFilter).length > 0 ? appliedFilter : props.defaultFilter || {};
    });

    const [pendingQuery, setPendingQuery] = useState<FilterQuery>(defaultFilter);

    const [filterQuery, setFilterQuery] = useState<FilterQuery>(pendingQuery);

    const [tempQuery, setTempQuery] = useState(pendingQuery ? Object.assign(query, pendingQuery) : {});
    const [applyClicked, setApplyClicked] = useState(false);

    const fetchActionQuery = props?.actions?.fetchAction?.(
        (props.searchOnApply && !applyClicked) ? skipToken : filterQuery
    );

    const userSettingQuery = useFindUserSettingsByGroupNameQuery(
        hasPermission(["UPRO-R"]) && props.userSettingGroup ? props.userSettingGroup : skipToken
    );

    const customerLevel: string = jwtDecode()?.customerLevel;

    let page: DataPage = getValueOrDefault(fetchActionQuery?.data, null);
    let data: DataItem[] = getValueOrDefault(page?.data || fetchActionQuery?.data, []);

    const displayText = getValueOrDefault(page?.displayingText, 'No data to display');
    const paginationDisplayText = getValueOrDefault(page?.displayingText, 'items');

    const [sort, setSort] = useState(defaultSort);

    const [pageNumber, setPageNumber] = useState(pageNum ? pageNum : 0);
    const [pageSize, setPageSize] = useState(defaultPageSize);

    const [pageCount, setPageCount] = useState(getValueOrDefault(page?.totalPages, 1));

    const refFilterQuery = useRef(pendingQuery);

    const [deleteAction, setDeleteAction] = useState(false);
    const [applyFilter, setApplyFilter] = useState(false);
    const [applyExport, setApplyExport] = useState(false);
    const [applyReset, setApplyReset] = useState(false);

    const [filterFormExpanded, setFilterFormExpanded] = useState(!!props.defaultFilterFormExpanded);
    const [filterFormKey, setFilterFormKey] = useState(1);
    const [filterValues, setFilterValues] = useState<Record<string, string>>({});

    const [showToggleDeleteConfirmation, setShowToggleDeleteConfirmation] = useState(false);
    useEffect(() => {
        refFilterQuery.current = pendingQuery;
    }, [pendingQuery]);

    useEffect(() => {
        setPageCount(getValueOrDefault(page?.totalPages, 1))
    }, [fetchActionQuery?.data]);

    const applyDefaultValues = () => {

        resetFilterComponents();

        const obj = props.resetFilterToDefault ? {...props.defaultFilter} : {
            // @ts-ignore
            sort: defaultFilter?.sort ? defaultFilter?.sort : "creationDate",
            // @ts-ignore
            sortOrder: defaultFilter?.sortOrder ? defaultFilter?.sortOrder : "asc"
        }
        refFilterQuery.current = obj;
        setPendingQuery(obj);
        setSort(defaultSort);

        setApplyFilter(true);
    }

    const resetFilterComponents = () => {
        for (const column of props.columns) {
            for (let filterOptions of column.filterOptions ?? []) {

                setFilterFormKey(filterFormKey + 1);

                if (filterOptions?.onDependencyUpdated && filterOptions.filterComponent.props.options) {
                    filterOptions.filterComponent = React.cloneElement(filterOptions.filterComponent, {
                        ...filterOptions.filterComponent.props,
                        options: [],
                    });
                }
            }
        }
    };


    const fetchPageData = () => {
        const sortOrder = sort.sortDescending ? "DESCENDING" : "ASCENDING";

        let query = {...pendingQuery} as any;

        if (pageSize && pageSize !== defaultPageSize) {
            query.pageSize = pageSize;
        }

        if (sort.sortColumn) {
            query.sort = sort.sortColumn;

            if (sortOrder) {
                query.sortOrder = sortOrder;
            }
        }

        if (pageNumber !== 0) {
            query.pageNumber = pageNumber;
        }

        setFilterQuery(query);

        if ((props.appendFilterToUrl === undefined) || props.appendFilterToUrl) {
            navigate({
                pathname: location.pathname,
                search: objectToQueryParams(query),
                state: {detail: objectToQueryParams(query)}
            });
        }
    };


    const deleteRecords = () => {
        props.actions.deleteAction(deleteItemList);
        setShowToggleDeleteConfirmation(false)
        setDeleteItemList([]);
    }
    const exportData = async (exportAction: NonNullable<DataTableActions["exportAction"]>, pendingQuery: FilterQuery) => {

        let sortOrder = sort?.sortDescending ? "ASCENDING" : "DESCENDING";

        let query = {
            ...pendingQuery,

        }
        await exportAction(query);
        setApplyExport(false);
    }

    useEffect(() => {
        fetchPageData();
    }, [pageNumber, pageSize, sort]);

    useEffect(() => {
        if (!applyFilter) {
            return;
        }

        fetchPageData();

        setApplyFilter(false);
    }, [applyFilter]);

    // TODO: Determine why this was here, it was causing searches to revert to defaultFilter when navigating between pages
    //  Default filter should be only set when someone hits "Clear" or navigates to it with no parameters
    useEffect(() => {
        // setPendingQuery(defaultFilter);
    }, [filterQuery]);

    useEffect(() => {
        if (!props.resetDefaultFilter) return;

        setApplyClicked(true);


        let obj = {
            // @ts-ignore
            sort: defaultFilter?.sort ? defaultFilter?.sort : "creationDate",
            // @ts-ignore
            sortOrder: defaultFilter?.sortOrder ? defaultFilter?.sortOrder : "asc"
        }
        setPendingQuery(obj);
        setSort(defaultSort);

        setApplyFilter(true);

    }, [props.resetDefaultFilter]);

    useEffect(() => {
        if (!applyExport) {
            return;
        }

        if (!props.actions?.exportAction) {
            return;
        }
        exportData(props.actions.exportAction, pendingQuery);


    }, [applyExport]);

    useEffect(() => {
        if (!deleteAction) {
            return;
        }

        if (!props?.actions?.deleteAction) {
            return;
        }

        setShowToggleDeleteConfirmation(true);
        setDeleteAction(false);
    }, [deleteAction]);

    const isLoading = fetchActionQuery?.isLoading || !Array.isArray(data);

    const initializeDefaultValues = (column: DataTableColumn) => {
        if (!column.filterOptions) {
            column.filterOptions = [];
        }

        if (!column.visibility) {
            column.visibility = getDefaultColumnVisibility();
        }


        if (column.canSort === undefined) {
            column.canSort = true;
        }
    };

    for (let column of props.columns) {
        initializeDefaultValues(column);
    }

    let tableColumns = useMemo(() => isLoading ? props.columns.map((column) =>
                ({...column, Cell: <Preloader show={true}/>})) :
            getFinalizedColumns(props.columns, userSettingQuery?.data),
        [isLoading, props.columns]
    );

    const tableData = useMemo(
        () => (isLoading ?
                Array<any>(!pageSize || pageSize === 0 ? defaultPageSize : pageSize).fill({}) :
                data
        ),
        [isLoading, data]
    );
    const safeData = Array.isArray(tableData) ? tableData : [];

    const {
        getTableProps,
        getTableBodyProps,
        headerGroups,
        rows,
        prepareRow,
    } = useTable({
            columns: tableColumns || [],
            data: safeData,
            manualSortBy: true,
            disableMultiSort: true,
            defaultCanSort: true,
            autoResetSortBy: false,
            manualPagination: true,
            pageCount: pageCount,
        } as any,
        useSortBy,
        // usePagination, // TODO: Might need to re-enable and re-work some of this to enable multi-page row selection. I don't anticipate needing this.
        // useRowSelect
    );

    const handleSort = useCallback(
        (column: any) => {
            if (!column.canSort) {
                return;
            }

            if (column.isSorted && !column.isSortedDesc) {
                column.clearSortBy();
            } else {
                column.toggleSortBy(!column.isSortedDesc, false);
            }

            setSort({
                sortColumn: column.overrideSortColumn ? column.overrideSortColumn : column.id,
                isSorted: column.isSorted,
                sortDescending: column.isSortedDesc
            });
        }, []);

    useEffect(() => {
        if (!applyReset) {
            return;
        }
        applyDefaultValues();
        props.setResetDefaultFilter?.(true);
        setApplyReset(false);
    }, [applyReset]);


    const applySettingsToHeaders = (column: DataTableColumn) => {
        let header = getHeaderByName(headerGroups, column.label);

        if (!header?.isVisible || !column.visibility) {
            return;
        }

        if (!column?.canSort) {
            header.canSort = column.canSort;
        }

        if (shouldHideFromUser(column?.visibility?.disallowedCustomerLevels, customerLevel)) {
            header.toggleHidden(true);
            return;
        }


        if (!column?.visibility?.canColumnShow && column.visibility?.canColumnShow !== undefined) {
            header.toggleHidden(true);
            return;
        }

        let label = "";
        if (typeof column?.label === 'object' && column?.label !== null && '$$typeof' in column?.label) {
            label = column?.label.props?.title;
        } else {
            label = column?.label;
        }

        const setting = userSettingQuery?.data?.find((item: any) => item.key === label);

        // If we have a setting that says to hide it, hide it.
        // If we have no setting, and it's hidden by default, hide it.
        const hide = (!setting && !column.visibility?.showByDefault && column.visibility?.canColumnShow !== undefined) || (setting && setting.value === 'hide');
        if (hide) {
            header.toggleHidden(true);
        }
    };

    // useEffect(handleFilterModalOptions, [FilterModalOptions]);

    const applyFilterOptionBinding = (column: DataTableColumn) => {
        bindDependents(column, tableColumns, refFilterQuery, setPendingQuery);
    }

    for (let column of props.columns) {
        applySettingsToHeaders(column);
        applyFilterOptionBinding(column);
    }

    const tableColumnsToShow = props.columns.filter((c: DataTableColumn) => !(c &&
        (shouldHideFromUser(c.visibility?.disallowedCustomerLevels, customerLevel))));

    const filterFieldsToShow = Array.isArray(tableColumnsToShow) && tableColumnsToShow
        .filter((c: DataTableColumn) => c && c.filterOptions && Object.keys(c.filterOptions).length) || [];

    const filterModalOptionsToShow =
        Array.isArray(tableColumnsToShow) &&
        tableColumnsToShow
            .flatMap((column: DataTableColumn) =>
                (column.filterOptions || []).filter(opt => opt.filterModalOption)
            )
            .map(opt => opt.filterModalOption) || [];

    const [selectedRows, setSelectedRows] = useState(new Set());

    const handleRowClick = (row: any) => {
        const {
            enableRowSelection,
            enableMultiRowSelection,
            rowIdAccessor,
            rowIdAccessorList,
            onSelectionChange,
            rowSelectionPredicate,
            defaultSelectedIds
        } = props.rowSelectionOptions || {};

        // Check if row is selectable
        const isRowSelectable = !rowSelectionPredicate || rowSelectionPredicate(row);

        // if (!enableRowSelection || !isRowSelectable || !enableMultiRowSelection) {
        //     return;
        // }

        const newSelectedRows = new Set(selectedRows);
        const rowId = Array.isArray(rowIdAccessorList) && rowIdAccessorList.length > 0
            ? rowIdAccessorList.reduce((acc, key) => {
                acc[key] = row.original[key];
                return acc;
            }, {} as Record<string, any>)
            : {...row.original};

        // @ts-ignore
        const isSelected = Array.from(selectedRows).some((row: any) => row.id === "eb11f34d-d4d5-4814-9cc3-cb6934a7cf31");

        if (enableMultiRowSelection) {
            if (isSelected) {
                newSelectedRows.delete(rowId);
            } else {
                newSelectedRows.add(rowId);
            }
        } else {
            newSelectedRows.clear();
            newSelectedRows.add(rowId);
        }

        setSelectedRows(newSelectedRows);

        if (props.hasBulkDelete) {
            setDeleteItemList((prev: any) =>
                prev.includes(rowId.id)
                    ? prev.filter((id: any) => id !== rowId.id)
                    : [...prev, rowId.id]
            );
        }


        if (onSelectionChange) {
            onSelectionChange(newSelectedRows, row);
        }
    };


    const getRowStyle = (row: any) => {
        const {
            enableRowSelection,
            rowSelectedStyle,
            rowUnselectableStyle,
            rowIdAccessor,
            rowSelectionPredicate,
            defaultSelectedIds
        } = props.rowSelectionOptions || {};

        if (!enableRowSelection) {
            return ''; // No specific styling if row selection is disabled
        }

        const rowId = getNestedValue(row.original, rowIdAccessor || 'id'); // Default to 'id' if rowIdAccessor is not provided

        // Check if row is selectable
        const isRowSelectable = !rowSelectionPredicate || rowSelectionPredicate(row);

        if (!isRowSelectable) {
            return rowUnselectableStyle || 'bg-surface-2 cursor-not-allowed'; // Apply unselectable style if row is not selectable
        }

        if (defaultSelectedIds) {
            defaultSelectedIds.forEach((el: any) => {
                selectedRows.add(el);
            });
        }

        const isSelected = Array.from(selectedRows).some((row: any) => getNestedValue(row, rowIdAccessor || 'id') === rowId);

        if (isSelected) {
            return rowSelectedStyle || '!bg-highlight-3 cursor-pointer'; // Apply selected style
        } else {
            return 'bg-text-0 cursor-pointer'; // Default unselected style
        }

    };

    function getNestedValue(obj: any, path: string): any {
        return path.split('.').reduce((acc, part) => acc?.[part], obj);
    }

    const handleCheckboxChange = (row: any) => {
        if (props?.rowSelectionOptions?.enableMultiRowSelection) {
            handleRowClick(row);
        }
    };

    return (
        <div className=''>
            {/*{debugLogging}*/}
            <>
                <div className="">
                    {/* Left side: Quick Filters if present */}
                    {(props.quickFilters && Array.isArray(props.quickFilters) && props.quickFilters.length > 0) && (
                        <div className={"flex justify-between items-center mb-2"}>
                            <div className="flex gap-1">
                                {props.quickFilters.map((cur, index) => {
                                    // @ts-ignore
                                    return (
                                        <div
                                            title={cur?.Label}
                                            key={index}
                                            onClick={() => {
                                                if (
                                                    !filterQuery.hasOwnProperty(quickFilterParams?.quickFilterKey) || (filterQuery.hasOwnProperty(quickFilterParams?.quickFilterKey) && filterQuery[quickFilterParams?.quickFilterKey] !== cur.className)
                                                ) {
                                                    setFilterQuery(cur.keyVal);
                                                } else {
                                                    setFilterQuery(props.quickFilters?.[0]?.defaultGridQuery || {});
                                                }
                                            }}
                                        >

                                            <div
                                                className={`inline-flex items-center justify-center text-center font-semibold mr-2 px-2.5 py-1.5 rounded-[11px] cursor-pointer w-[53px] ${cur.keyVal?.customStyle} ${filterQuery.hasOwnProperty(quickFilterParams?.quickFilterKey) &&
                                                filterQuery[quickFilterParams?.quickFilterKey] === cur.className &&
                                                _.isEqual(
                                                    filterQuery[Object.keys(cur.keyVal)[0]],
                                                    cur.keyVal[Object.keys(cur.keyVal)[0]]
                                                ) ? "bg-surface-3" : ""}`}>
                                                {filterQuery.hasOwnProperty(quickFilterParams?.quickFilterKey) &&
                                                filterQuery[quickFilterParams?.quickFilterKey] === cur.className &&
                                                _.isEqual(
                                                    filterQuery[Object.keys(cur.keyVal)[0]],
                                                    cur.keyVal[Object.keys(cur.keyVal)[0]]
                                                ) && fetchActionQuery?.isFetching ? <TailSpinLoader/> : cur?.Cell}
                                            </div>
                                            {/*{cur?.Cell}*/}
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    )}

                    {/* Right side: Always Extra Buttons */}

                    <div className={`${isExternal ? 'top-[7px]' : ' top-[60px]'} flex gap-2 absolute right-5`}>
                        {props.buttonOptions?.extraButtons?.map((button, index) => (
                            hasPermission(button.permissions || []) && (
                                <div key={index}>
                                    {button.button}
                                </div>
                            )
                        ))}

                        {filterFieldsToShow?.length > 0 && !props.renderOptions?.skipFilterButtonRender && (
                            <IconButton
                                className="h-[35px] min-w-[52px] rounded-[11px]"
                                icon={<FaMagnifyingGlass size={14} className={''}/>}
                                hasBackground={true}
                                onClick={() => setFilterFormExpanded(!filterFormExpanded)}
                                title="Search"
                            />
                        )}

                        {props?.buttonOptions && hasPermission(props?.buttonOptions?.addButton?.permissions || []) && props.buttonOptions?.addButton?.button}
                    </div>
                </div>

                {!!props.renderOptions?.skipFilterRender || !filterFormExpanded ||
                    <div
                        className={`pb-2 my-2 border-2  ${props.isHoa ? 'border-highlight-1' : 'border-highlight-0'
                        } rounded-sm`}
                        id="grid"
                    >

                        <div className='md:grid md:grid-cols-12 gap-2 p-4' key={filterFormKey}>
                            {filterFieldsToShow.map((filterField: DataTableColumn) => filterField?.filterOptions?.length &&
                                <div
                                    className={filterFieldsToShow?.length > 2 ? 'col-span-4 justify-between flex gap-2' : ' col-span-6 justify-between flex'}>
                                    {
                                        filterField.filterOptions?.map((filterOptions: FilterOptions) => filterOptions && Object.keys(filterOptions).length &&
                                            <div className="w-11/12 sm:mt-2">
                                                <label className='text-surface-0'>
                                                    {!!filterOptions?.hideLabel ? '' : (filterOptions.overrideFilterLabel || filterField.label)}
                                                </label>
                                                <div className={"mt-1"}>
                                                    {
                                                        !filterOptions?.dependentColumns ? React.cloneElement(filterOptions?.filterComponent, {
                                                            onChange: (event: any) => updateFilterQuery(filterOptions.overrideFilterAccessor || filterField.accessor,
                                                                event,
                                                                refFilterQuery.current,
                                                                setPendingQuery),
                                                            onKeyDown: (event: any) => {
                                                                if (event.key === 'Enter') {
                                                                    event.preventDefault();
                                                                    setApplyFilter(true);

                                                                    return false;
                                                                }
                                                            }
                                                        }) : React.cloneElement(filterOptions?.filterComponent, {
                                                            onKeyDown: (event: any) => {
                                                                if (event.key === 'Enter') {
                                                                    event.preventDefault();
                                                                    setApplyFilter(true);

                                                                    return false;
                                                                }
                                                            }
                                                        })
                                                    }
                                                </div>
                                            </div>
                                        )
                                    }
                                </div>
                            )
                            }
                        </div>

                        {/*Filter Button Row*/}
                        {!!props.renderOptions?.skipButtonSectionRender ||
                            <div className=' inline-flex gap-1 px-4 pt-4'>
                                {/*TODO: Would be nice if all of our "submit" actions, like delete/export/apply, were in a <ButtonWithMenu/>*/}
                                {/*Export Button*/}
                                {props.actions?.exportAction &&
                                    <Button isLoading={isLoading || applyExport}
                                            isDisabled={props.actions.exportDisabled}
                                            btnText='Export'
                                            type={'confirm'}
                                            onClick={() => setApplyExport(true)}/>
                                }
                                {/*Clear Button*/}
                                <Button isLoading={isLoading}
                                        btnText='Clear'
                                        type={'cancel'}
                                        onClick={() => {
                                            setApplyReset(true);
                                            props.setResetDefaultFilter?.(true);
                                        }} isHoa={props.isHoa}/>

                                {/*Apply Button*/}
                                {!props.isHideApply &&
                                    <Button
                                        isLoading={isLoading || fetchActionQuery?.status === "pending"}
                                        btnText="Apply"
                                        type="confirm"
                                        onClick={() => {
                                            setApplyFilter(true);
                                            setApplyClicked(true);
                                        }}
                                        isHoa={props.isHoa}
                                    />
                                }
                                {props.hasBulkDelete &&
                                    <Button
                                        isLoading={props?.actions?.deleteAction.isLoading || props?.actions?.deleteAction?.status === "pending"}
                                        btnText="Delete"
                                        type="confirm"
                                        onClick={() => {
                                            setDeleteAction(true);
                                        }}
                                        isDisabled={deleteItemList.length === 0}
                                        isHoa={props.isHoa}
                                    />
                                }

                            </div>
                        }
                    </div>
                }
            </>
            {
                filterModalOptionsToShow?.length > 0 && <div className="flex flex-wrap gap-4 mb-4">
                    {filterModalOptionsToShow?.map((modalOpt: any, index: number) => (
                        <div
                            key={index}
                            className={`flex items-center border border-surface-5 rounded-md p-2 ${filterModalOptionsToShow.length === 1 ? 'w-full' : 'w-full md:w-[48%]'
                            }`}
                        >
                            <input
                                type="text"
                                placeholder={modalOpt.filterModalPlaceHolder}
                                className="flex-1 outline-none"
                                value={filterValues[modalOpt.filterModalKey] || ''}
                                onChange={(e) => {
                                    const key = modalOpt.filterModalKey;
                                    const value = e.target.value;

                                    // Mimic updateFilterQuery behavior
                                    refFilterQuery.current = {
                                        ...refFilterQuery.current,
                                        [key]: value
                                    };

                                    setPendingQuery({...refFilterQuery.current});
                                    setFilterValues(prev => ({...prev, [key]: value}));
                                }}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter') {
                                        e.preventDefault();
                                        setApplyFilter(true); // Apply immediately
                                    }
                                }}
                            />

                            {modalOpt.filterModalIcon && (
                                <span
                                    onClick={() => {
                                        const key = modalOpt.filterModalKey;
                                        const value = filterValues[key];

                                        // Optional: You can trigger apply logic here too
                                        refFilterQuery.current = {
                                            ...refFilterQuery.current,
                                            [key]: value
                                        };

                                        setPendingQuery({...refFilterQuery.current});
                                        setApplyFilter(true);
                                    }}
                                    className="cursor-pointer ml-2"
                                >
                                    {modalOpt.filterModalIcon}
                                </span>
                            )}
                        </div>
                    ))}

                </div>
            }


            {!(props.searchOnApply && !applyClicked) && (

                !!props.renderOptions?.skipTableRender ||
                <>
                    {!!props.renderOptions?.skipPaginationRender ||
                        userSettingQuery?.data && computePageControl(userSettingQuery?.data, 'TOP') &&
                        <Pagination
                            displayText={paginationDisplayText}
                            pageCount={pageCount}
                            setPageNumber={setPageNumber}
                            pageNumber={pageNumber}
                            setExternalPageSize={setPageSize}
                            groupName={props.userSettingGroup || ''}
                            props={props}
                            isHoa={props.isHoa}
                        />
                    }

                    {!!props.renderOptions?.skipTableRender ||

                        <div className={"overflow-auto"}>
                            <table {...getTableProps()} className={`text-sm  w-full `}>
                                <thead className={props?.classname ? props?.classname : "bg-highlight-0"}>
                                {
                                    headerGroups.map(headerGroup => (
                                        <tr {...headerGroup.getHeaderGroupProps()}>
                                            {props?.hasBulkDelete && (
                                                <th/>
                                            )}
                                            {
                                                headerGroup.headers.map((header: any) => (
                                                    <th {...header.getHeaderProps(header.getSortByToggleProps())}
                                                        onClick={() => handleSort(header)}
                                                        className={props?.classname ? props.classname + " text-left whitespace-nowrap" : "bg-highlight-0 text-text-0 text-left whitespace-nowrap"}
                                                    >
                                                        {header.render('label')}

                                                        {/* Add a sort direction indicator */}
                                                        <span className="noselect">&ensp;
                                                            <i
                                                                className={`${props?.classname ? props.classname : "text-text-0"} cursor fas ${(header?.canSort ? header?.isSorted ? header?.isSortedDesc ? 'fa-sort-down' : 'fa-sort-up' : 'fa-sort' : '')}`}
                                                            />
                                                            </span>
                                                    </th>
                                                ))
                                            }
                                        </tr>
                                    ))
                                }
                                </thead>

                                <tbody {...getTableBodyProps()}>

                                {
                                    rows?.map((row: any) => {
                                        prepareRow(row);
                                        return (

                                            <tr id={row?.id} {...row.getRowProps()} // Default to 'id' if rowIdAccessor is not provided
                                                onClick={() => {
                                                    if (props?.rowSelectionOptions?.enableRowSelection) {
                                                        handleRowClick(row);
                                                    }
                                                }}
                                                className={` even:bg-surface-7 hover:bg-surface-7 ${getRowStyle(row)}`}>
                                                {props.hasBulkDelete && (
                                                    <td>
                                                        {isLoading ? <SkeletonLoader count={1}/> : <input
                                                            type="checkbox"
                                                            checked={deleteItemList.includes(row.original.id)}
                                                            onChange={() => handleCheckboxChange(row)}
                                                        />
                                                        }
                                                    </td>
                                                )}

                                                {
                                                    row.cells.map((cell: any) => {
                                                        return (
                                                            <td {...cell.getCellProps()}>
                                                                {
                                                                    isLoading ?
                                                                        <SkeletonLoader count={1}/> :
                                                                        cell.render('Cell')
                                                                }
                                                            </td>
                                                        )
                                                    })
                                                }
                                            </tr>
                                        )
                                    })
                                }

                                {(page?.data?.length === 0 || (fetchActionQuery?.status === "fulfilled" && !page && data?.length === 0)) && (
                                    <tr>
                                        <td colSpan={5} className="text-surface-0 p-2">{displayText}

                                            {props.formCheckForModals === true && props.hasCreateFormBtn && Object.keys(props.hasCreateFormBtn).length > 0 &&
                                                <span className="ml-2">
                                                        <a className="text-highlight-4 hover:text-highlight-7 cursor"
                                                           target="_blank"
                                                           href={props.hasCreateFormBtn.path}>{props.hasCreateFormBtn.label}</a>
                                                    </span>
                                            }
                                        </td>
                                    </tr>
                                )}
                                </tbody>
                            </table>
                        </div>

                    }

                    <div className='mt-2'>
                        {!props.renderOptions?.skipPaginationRender && page?.data?.length > 0 && (
                            (!userSettingQuery || computePageControl(userSettingQuery.data, "BOTTOM")) && (
                                <Pagination
                                    displayText={paginationDisplayText}
                                    pageCount={pageCount}
                                    setPageNumber={setPageNumber}
                                    pageNumber={pageNumber}
                                    setExternalPageSize={setPageSize}
                                    groupName={props.userSettingGroup || ''}
                                    props={props}
                                    isHoa={props.isHoa}
                                />
                            )
                        )}


                    </div>
                </>
            )
            }
            {
                showToggleDeleteConfirmation &&
                <ConfirmationModal
                    title={tempQuery?.deleted === true && filterQuery?.deleted === true ? 'Bulk Restore Confirmation' : "Bulk Delete Confirmation"}
                    bodyMessage={tempQuery?.deleted === true ? 'Are you sure you want to restore all selected items?' : "Are you sure you want to delete all selected items?"}
                    onSubmit={deleteRecords}
                    show={showToggleDeleteConfirmation}
                    setShow={setShowToggleDeleteConfirmation}
                />
            }
        </div>
    )
}
